import {CloseOutlined, ExportOutlined, MoreOutlined, PlusOutlined, QuestionCircleFilled} from '@ant-design/icons';
import {
    Button,
    Card,
    Checkbox,
    Dropdown,
    Flex,
    Image,
    List,
    MenuProps,
    Space,
    Switch,
    Table,
    TablePaginationConfig,
    TableProps,
    Tag,
    Tooltip,
    Typography,
} from 'antd';
import Search from 'antd/es/input/Search';
import {ColumnFilterItem, SorterResult} from 'antd/es/table/interface';
import {ColumnsType} from 'antd/lib/table';
import dayjs from 'dayjs';
import React, {useEffect, useState} from 'react';
import {Link, useNavigate} from 'react-router-dom';

import {ApiQuery} from '../../../gen/api';
import {useAdminGroupSaleListQuery} from '../../../gen/api/apis';
import {
    AdminCategory,
    AdminMarketplace,
    AdminMarketplaceType,
    AdminOrderStatusType,
    AdminPalletOrderList,
    AdminProductList,
    AdminProductListImage,
    AdminProductListOrders,
    AdminProductStatus,
    ProductConsistency,
} from '../../../gen/api/models';
import {useNotification} from '../../../NotificationContext';
import {useScreenInfo} from '../../../ResponsiveGate';
import {downloadBlobAsFile} from '../../helpers/files';
import {marketplaceLabels, productConsistencies, productStatuses} from '../../helpers/labels';
import {orderPaymentTypeLabel} from '../../helpers/orders';
import {groupSaleStatusBadge, groupSaleStatusLabel, productStatusBadge, productStatusLabel} from '../../helpers/status';
import {BatchLabelPrint, PrintableProducts} from './components/BatchLabelPrint';
import {BatchStatusChangeModal, ModalStatusProduct} from './components/BatchStatusChangeModal';
import {BrokenLinkTag} from './components/BrokenLinkTag';
import {GroupSaleAssignModal, ModalGroupSaleProduct} from './components/GroupSaleAssignModal';
import {PriceAnalyticsIcon} from './components/PriceAnalyticsIcon';
import {useRunPriceAnalytics} from './components/useRunPriceAnalytics';

const {Title} = Typography;

// default number of products shown per page in the list view
const DEFAULT_PAGE_SIZE = 50;

// available page sizes for the paginator, extended up to 200 items
const PAGE_SIZE_OPTIONS = ['10', '20', '50', '100', '200'];

// Sentinel value for the "Show Sold" entry in the Status column filter.
// Sold products are hidden by default on the server side, this option sends showSold=true instead of status=sold.
const SHOW_SOLD_FILTER_VALUE = 'showSold';

type StatusFilterValue = AdminProductStatus | typeof SHOW_SOLD_FILTER_VALUE;

// split raw values selected in the Status filter into api query params
function buildStatusQuery(statuses?: StatusFilterValue[]) {
    const realStatuses = (statuses ?? []).filter((s) => s !== SHOW_SOLD_FILTER_VALUE);
    return {
        // omit the param when nothing is selected, an empty list would be serialized as status= and rejected by the server
        status: (realStatuses.length ? realStatuses : undefined) as AdminProductStatus[] | undefined,
        showSold: (statuses ?? []).includes(SHOW_SOLD_FILTER_VALUE),
    };
}

// The only value of the Link column filter: products with a broken listing link.
const BROKEN_LINK_FILTER_VALUE = 'broken';

const statusFilters: ColumnFilterItem[] = [
    ...productStatuses
        .filter((m) => m.key !== AdminProductStatus.ProductStatusSold)
        .map((m): ColumnFilterItem => ({text: m.label, value: m.key})),
    {text: 'Show Sold', value: SHOW_SOLD_FILTER_VALUE},
];

type ProductFilters = {
    categoryIDs?: number[];
    palletOrderIDs?: number[];
    status?: StatusFilterValue[];
    consistency?: ProductConsistency[];
    consistencyDetails?: string[];
    groupSaleIDs?: string[];
    marketplaces?: AdminMarketplaceType[];
    brokenLink?: (typeof BROKEN_LINK_FILTER_VALUE)[];
    duplicates?: boolean;
};

interface TableParams {
    pagination?: TablePaginationConfig;
    sortField?: SorterResult<any>['field'];
    sortOrder?: SorterResult<any>['order'];
    filters?: ProductFilters;
}

type TableRowSelection<T extends object = object> = TableProps<T>['rowSelection'];

function checkIfFiltersExist(filters: Record<string, any> | undefined) {
    // check if any of the filters are defined
    if (!filters) return false;
    const items = Object.keys(filters).filter((key) => {
        if (filters[key] !== undefined && filters[key] !== null) {
            return true;
        }
    });
    return items.length > 0;
}

export const ProductListPage = () => {
    const {screenMap} = useScreenInfo();

    const notify = useNotification();
    const navigate = useNavigate();
    const params = localStorage.getItem('productFilters');
    const filters = params ? (JSON.parse(params) as ProductFilters) : undefined;
    const searchParam = localStorage.getItem('productSearch');

    const paginationParams = new URLSearchParams(window.location.search);

    // store pagination in query params

    const preloadedFilters: ProductFilters | undefined = checkIfFiltersExist(filters) ? filters : undefined;
    // restore the last used page size from local storage, fall back to default when missing or invalid
    const savedPageSize = Number(localStorage.getItem('productListPageSize'));
    const initialPageSize = PAGE_SIZE_OPTIONS.includes(String(savedPageSize)) ? savedPageSize : DEFAULT_PAGE_SIZE;

    const [tableParams, setTableParams] = useState<TableParams>({
        filters: preloadedFilters,
        pagination: {
            // get the current page from query params
            current: paginationParams.get('page') ? parseInt(paginationParams.get('page')!) : 1,
            pageSize: paginationParams.get('pageSize') ? parseInt(paginationParams.get('pageSize')!) : initialPageSize,
        },
    });

    const [searchValue, setSearchValue] = useState<string | undefined>(searchParam || undefined);
    const [products, setProducts] = useState<AdminProductList[]>();
    const [categories, setCategories] = React.useState<AdminCategory[]>();
    const [pallets, setPallets] = React.useState<AdminPalletOrderList[]>();
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const priceAnalytics = useRunPriceAnalytics();
    const [selectedRowsAll, setSelectedRowsAll] = useState<boolean>(false);
    // bigImageMode based in local storage
    const [bigImageMode, setBigImageMode] = useState<boolean>(
        localStorage.getItem('productListBigImageMode')
            ? localStorage.getItem('productListBigImageMode') === 'true'
            : false
    );
    const [labelPrintMode, setLabelPrintMode] = useState<boolean>(false);
    const [modalBatchLabelPrint, setModalBatchLabelPrint] = useState<boolean>(false);
    const [modalGroupSaleAssign, setModalGroupSaleAssign] = useState<boolean>(false);
    const [modalBatchStatusChange, setModalBatchStatusChange] = useState<boolean>(false);

    const actionList: MenuProps['items'] = [
        {
            label: bigImageMode ? 'Small Images' : 'Big Images',
            key: '1',
        },
        {
            label: 'Download Images',
            key: '2',
        },
        {
            label: labelPrintMode ? 'Disable Label Print Mode' : 'Enable Label Print Mode',
            key: '3',
            onClick: () => {
                setLabelPrintMode(!labelPrintMode);
            },
        },
        {
            label: 'Batch Print Labels',
            key: '4',
            onClick: () => {
                setModalBatchLabelPrint(true);
            },
            disabled: !selectedRowKeys.length,
        },
        {
            label: 'Group Sale Assign',
            key: '5',
            onClick: () => {
                setModalGroupSaleAssign(true);
            },
            disabled: !selectedRowKeys.length,
        },
        {
            label: `Run Price Analytics${selectedRowKeys.length ? ` (${selectedRowKeys.length})` : ''}`,
            key: '6',
            onClick: () => priceAnalytics.run(selectedRowKeys.map((key) => key as string)),
            disabled: !selectedRowKeys.length || priceAnalytics.isRunning,
        },
        {
            label: `Change Status${selectedRowKeys.length ? ` (${selectedRowKeys.length})` : ''}`,
            key: '7',
            onClick: () => {
                setModalBatchStatusChange(true);
            },
            disabled: !selectedRowKeys.length,
        },
    ];

    const onClickActions: MenuProps['onClick'] = ({key}) => {
        switch (key) {
            case '1':
                setBigImageMode(!bigImageMode);
                localStorage.setItem('productListBigImageMode', (!bigImageMode).toString());
                break;
            case '2':
                batchDownloadImages.mutate({
                    params: {
                        ids: selectedRowKeys.length ? selectedRowKeys.map((key) => key as string) : undefined,
                        all: selectedRowsAll,
                        filters: {
                            search: searchValue,
                            categoryIDs: tableParams.filters?.categoryIDs,
                            palletOrderIDs: tableParams.filters?.palletOrderIDs,
                            ...buildStatusQuery(tableParams.filters?.status),
                            consistency: tableParams.filters?.consistency,
                            marketplace: tableParams.filters?.marketplaces,
                            brokenLink: !!tableParams.filters?.brokenLink?.length,
                            duplicates: tableParams.filters?.duplicates,
                        },
                    },
                });
                break;
        }
    };

    const batchDownloadImages = ApiQuery.useGetAdminDownloadProductImagesMutation({
        onSuccess(data) {
            if (data.size !== 0) {
                downloadBlobAsFile(data, `images.zip`);
            }
        },
        onError() {
            notify.openNotificationWithIcon('error', 'Error downloading images');
        },
    });

    const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
        if (selectedRowsAll) {
            setSelectedRowsAll(false);
        }
        setSelectedRowKeys(newSelectedRowKeys);
    };

    const rowSelection: TableRowSelection<AdminProductList> = {
        selectedRowKeys,
        columnTitle: () => {
            return (
                <Checkbox
                    indeterminate={
                        selectedRowKeys.length !== 0 &&
                        selectedRowKeys.length !== tableParams.pagination?.total &&
                        !selectedRowsAll
                    }
                    checked={selectedRowKeys.length !== 0 || selectedRowsAll}
                    onChange={() => {
                        if (selectedRowsAll) {
                            setSelectedRowKeys([]);
                            setSelectedRowsAll(false);
                        } else if (selectedRowKeys.length === 0) {
                            setSelectedRowKeys(products?.map((product) => product.id) || []);
                        } else if (selectedRowKeys.length !== tableParams.pagination) {
                            setSelectedRowsAll(true);
                            setSelectedRowKeys(products?.map((product) => product.id) || []);
                        } else {
                            setSelectedRowKeys([]);
                            setSelectedRowsAll(false);
                        }
                    }}
                />
            );
        },
        onChange: onSelectChange,
    };

    const productsQuery = ApiQuery.usePostAdminProductListMutation({
        onSuccess(data) {
            // set pagination in query params
            const searchParams = new URLSearchParams(window.location.search);
            // calculate the current page. if data.items are empty or less than page size, set page to 1
            const currentPage =
                (data.total || 0) < tableParams.pagination?.pageSize! ? 1 : tableParams.pagination?.current!;
            console.log('productsQuery', {
                data: data,
                currentPage: currentPage,
                lenght: data.items?.length,
                pageSize: tableParams.pagination?.pageSize,
            });
            setTableParams((prev) => ({
                ...prev,
                pagination: {
                    ...prev.pagination!,
                    current: currentPage,
                    total: data.total,
                },
            }));
            setProducts(data.items);

            searchParams.set('page', currentPage.toString());
            searchParams.set('pageSize', (tableParams.pagination?.pageSize || 30).toString());
            const newRelativePathQuery = window.location.pathname + '?' + searchParams.toString();
            window.history.pushState(null, '', newRelativePathQuery);
            if (selectedRowsAll) {
                setSelectedRowKeys(data?.items!.map((product) => product.id));
            } else {
                setSelectedRowKeys([]);
            }
        },
    });

    const categoryQuery = ApiQuery.useGetAdminProductCategoryListQuery({
        onSuccess(data) {
            setCategories(data.items);
        },
        refetchOnMount: 'always',
    });

    const palletOrdersQuery = ApiQuery.useAdminGetAllPalletsQuery({
        onSuccess(data) {
            setPallets(data.items);
        },
        refetchOnMount: 'always',
    });

    const productConsistencyDetails = ApiQuery.useGetAdminProductsConsistencyDetailsQuery({
        refetchOnMount: 'always',
    });

    const groupSalesQuery = useAdminGroupSaleListQuery({
        refetchOnMount: 'always',
    });

    const storeFilters = () => {
        // store the current filters in local storage
        const localStorageFilters: ProductFilters = {
            categoryIDs: tableParams.filters?.categoryIDs,
            palletOrderIDs: tableParams.filters?.palletOrderIDs,
            status: tableParams.filters?.status,
            consistency: tableParams.filters?.consistency,
            consistencyDetails: tableParams.filters?.consistencyDetails,
            groupSaleIDs: tableParams.filters?.groupSaleIDs,
            marketplaces: tableParams.filters?.marketplaces,
            brokenLink: tableParams.filters?.brokenLink,
            duplicates: tableParams.filters?.duplicates,
        };

        if (checkIfFiltersExist(localStorageFilters)) {
            console.log('storeFilters', localStorageFilters);
            localStorage.setItem('productFilters', JSON.stringify(localStorageFilters));
        } else {
            console.log('storeFilters - removing filters');
            localStorage.removeItem('productFilters');
        }
        if (searchValue) {
            console.log('storeSearch', searchValue);
            localStorage.setItem('productSearch', searchValue);
        } else {
            console.log('storeSearch - removing search');
            localStorage.removeItem('productSearch');
        }
    };

    const requestFilters = () => {
        console.log('requested filters', {filters: tableParams.filters, search: searchValue});
        productsQuery.mutate({
            limit: tableParams.pagination?.pageSize,
            offset: (tableParams.pagination?.current! - 1) * tableParams.pagination?.pageSize!,
            search: searchValue,
            categoryIDs: tableParams.filters?.categoryIDs,
            palletOrderIDs: tableParams.filters?.palletOrderIDs,
            ...buildStatusQuery(tableParams.filters?.status),
            consistency: tableParams.filters?.consistency,
            consistencyDetails: tableParams.filters?.consistencyDetails,
            groupSaleIDs: tableParams.filters?.groupSaleIDs,
            marketplace: tableParams.filters?.marketplaces,
            brokenLink: !!tableParams.filters?.brokenLink?.length,
            duplicates: tableParams.filters?.duplicates,
        });
    };

    const clearFilters = () => {
        setTableParams({
            ...tableParams,
            filters: undefined,
        });
        setSearchValue('');
        localStorage.removeItem('productFilters');
    };

    useEffect(() => {
        console.log('useEffect tableParams', tableParams);
        requestFilters();
        storeFilters();
    }, [
        tableParams.pagination?.current,
        tableParams.pagination?.pageSize,
        tableParams?.sortOrder,
        tableParams?.sortField,
        // partial filters except search
        JSON.stringify({
            categoryIDs: tableParams.filters?.categoryIDs,
            palletOrderIDs: tableParams.filters?.palletOrderIDs,
            status: tableParams.filters?.status,
            consistency: tableParams.filters?.consistency,
            consistencyDetails: tableParams.filters?.consistencyDetails,
            groupSaleIDs: tableParams.filters?.groupSaleIDs,
            marketplaces: tableParams.filters?.marketplaces,
            brokenLink: tableParams.filters?.brokenLink,
            duplicates: tableParams.filters?.duplicates,
        }),
    ]);

    // persist the last used page size so a reload keeps the user's pagination choice
    useEffect(() => {
        const pageSize = tableParams.pagination?.pageSize;
        if (pageSize) {
            localStorage.setItem('productListPageSize', String(pageSize));
        }
    }, [tableParams.pagination?.pageSize]);

    const isFirstRender = React.useRef(true);
    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        const delayDebounceFn = setTimeout(() => {
            console.log('useEffect search', searchValue);
            requestFilters();
            storeFilters();
        }, 500);

        return () => clearTimeout(delayDebounceFn);
    }, [searchValue]);

    const handleTableChange: TableProps<AdminProductList>['onChange'] = (pagination, filters, sorter) => {
        console.log('Various parameters', pagination, filters, sorter);
        setTableParams({
            pagination,
            sortOrder: Array.isArray(sorter) ? undefined : sorter.order,
            sortField: Array.isArray(sorter) ? undefined : sorter.field,
            filters,
        });
    };

    const columns: ColumnsType<AdminProductList> = [
        {
            dataIndex: 'images',
            key: 'images',
            width: bigImageMode ? 150 : 20,
            fixed: screenMap.md ? 'left' : undefined,
            render: (images: Array<AdminProductListImage>) => (
                <>
                    {images?.length && (
                        <Image
                            width={bigImageMode ? 150 : 20}
                            preview={false}
                            src={images?.find((img) => img.isPrimary)?.thumbnailPath}
                        />
                    )}
                </>
            ),
        },
        {
            title: (
                <Flex
                    justify={'space-between'}
                    align={'center'}
                >
                    <span>ID</span>
                    <Flex
                        align={'center'}
                        gap={4}
                    >
                        <div style={{fontWeight: 'normal'}}>Duplicates</div>
                        <Switch
                            size={'small'}
                            checked={tableParams.filters?.duplicates}
                            onChange={(checked) =>
                                setTableParams({
                                    ...tableParams,
                                    filters: {
                                        ...tableParams.filters,
                                        duplicates: checked,
                                    },
                                })
                            }
                        />
                    </Flex>
                </Flex>
            ),
            dataIndex: 'listingNumber',
            key: 'id',
            minWidth: 120,
            fixed: screenMap.md ? 'left' : undefined,
            render: (id, product) => {
                const extra = product.asin && (
                    <a>
                        <Tooltip title={`Find Product: ${product.asin}`}>
                            <Button
                                size={'small'}
                                type="link"
                                onClick={() => window.open(`https://www.google.com/search?q=${product.asin}`, '_blank')}
                                icon={<ExportOutlined />}
                                iconPosition={'end'}
                            >
                                {` / ASIN`}
                            </Button>
                        </Tooltip>
                    </a>
                );
                if (labelPrintMode) {
                    return (
                        <Typography.Text
                            copyable={{
                                text: id,
                                tooltips: 'Copy for printing label',
                            }}
                            style={{marginLeft: 5}}
                        >
                            {id}
                        </Typography.Text>
                    );
                }
                return (
                    <Flex
                        align={'center'}
                        gap={5}
                    >
                        <Link to={`/products/${product.id}`}>{id}</Link>
                        {extra}
                    </Flex>
                );
            },
        },
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            render: (text: string, product: AdminProductList) => (
                <Flex
                    align={'center'}
                    justify={'space-between'}
                >
                    <Flex
                        align={'center'}
                        gap={6}
                        style={{minWidth: 0}}
                    >
                        <PriceAnalyticsIcon
                            status={product.status}
                            analytics={product.analytics}
                        />
                        <div
                            style={{maxWidth: 300, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}
                        >
                            {text}
                        </div>
                    </Flex>
                    {labelPrintMode && (
                        <Typography.Text
                            copyable={{
                                text: `${text.trim().slice(0, 120)}${text.length > 120 && '...'}`,
                                tooltips: 'Copy for printing label',
                            }}
                            style={{marginLeft: 5}}
                        />
                    )}
                </Flex>
            ),
        },
        {
            title: 'Link',
            dataIndex: 'marketplaces',
            key: 'brokenLink',
            filteredValue: tableParams.filters?.brokenLink || null,
            filters: [{text: 'Broken', value: BROKEN_LINK_FILTER_VALUE}],
            filterMultiple: false,
            render: (marketplaces: Array<AdminMarketplace> | undefined) => (
                <Flex
                    vertical
                    gap={2}
                >
                    {marketplaces?.map((marketplace) => (
                        <BrokenLinkTag
                            key={marketplace.id}
                            marketplace={marketplace}
                        />
                    ))}
                </Flex>
            ),
        },
        {
            title: (
                <>
                    PP{' '}
                    <Tooltip
                        placement="right"
                        title="Purchace Price"
                    >
                        <QuestionCircleFilled />
                    </Tooltip>
                </>
            ),
            dataIndex: 'purchasePrice',
            key: 'purchasePrice',
            render: (price) => <>{price != 0 ? price : 'N/A'}</>,
        },
        {
            title: 'MSRP',
            dataIndex: 'msrp',
            key: 'msrp',
            render: (price) => <>${price}</>,
        },
        {
            title: (
                <>
                    LRP{' '}
                    <Tooltip
                        placement="right"
                        title="Lowest Resale Price"
                    >
                        <QuestionCircleFilled />
                    </Tooltip>
                </>
            ),
            dataIndex: 'lowestResalePrice',
            key: 'lowestResalePrice',
            render: (price) => <>${price}</>,
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            filteredValue: tableParams.filters?.status || null,
            render: (status: AdminProductStatus) => {
                return <Tag color={productStatusBadge(status)}>{productStatusLabel(status)}</Tag>;
            },
            filters: statusFilters,
        },
        {
            title: 'Consistency',
            dataIndex: 'consistency',
            key: 'consistency',
            filteredValue: tableParams.filters?.consistency || null,
            render: (consistency: ProductConsistency) => {
                const color = consistency === ProductConsistency.ProductConsistencyNew ? 'green' : 'orange';
                return <Tag color={color}>{consistency.toUpperCase()}</Tag>;
            },
            filters: productConsistencies.map((m): ColumnFilterItem => ({text: m.label, value: m.key})),
            width: 50,
        },
        {
            title: 'Consistency Details',
            dataIndex: 'consistencyDetails',
            key: 'consistencyDetails',
            filteredValue: tableParams.filters?.consistencyDetails || null,
            render: (consistency: string[]) => {
                return consistency.map((c) => <Tag key={c}>{c}</Tag>);
            },
            filters: productConsistencyDetails.data?.items?.map((m): ColumnFilterItem => ({text: m, value: m})),
            filterSearch: true,
        },
        {
            title: 'Group Sale',
            dataIndex: 'groupSaleId',
            key: 'groupSaleIDs',
            filters: groupSalesQuery.data?.groupSales?.map((m): ColumnFilterItem => ({text: m.name, value: m.id})),
            filteredValue: tableParams.filters?.groupSaleIDs || null,
            render: (id) => {
                const groupSale = groupSalesQuery.data?.groupSales?.find((g) => g.id === id);
                if (groupSale) {
                    return (
                        <Flex
                            justify={'space-between'}
                            gap={5}
                        >
                            <Link to={`/group-sales/${id}`}>{groupSale.name}</Link>
                            <Tag
                                color={groupSaleStatusBadge(groupSale.status)}
                                style={{marginRight: 0}}
                            >
                                {groupSaleStatusLabel(groupSale.status)}
                            </Tag>
                        </Flex>
                    );
                } else {
                    return <span>-</span>;
                }
            },
        },
        {
            title: 'Marketplaces',
            dataIndex: 'marketplaces',
            key: 'marketplaces',
            filteredValue: tableParams.filters?.marketplaces || null,
            render: (marketplaces: Array<AdminMarketplace>) => {
                if (marketplaces?.length === 4) {
                    return <Tag color={'green'}>ALL</Tag>;
                } else {
                    return (
                        <Flex>
                            {marketplaces?.map((marketplace) => <Tag key={marketplace.id}>{marketplace.name}</Tag>)}
                        </Flex>
                    );
                }
            },
            filterMode: 'tree',
            filters: marketplaceLabels.map((m): ColumnFilterItem => ({text: m.label, value: m.key})),
        },
        {
            title: 'Orders',
            dataIndex: 'orders',
            key: 'orders',
            render: (orders: Array<AdminProductListOrders> | undefined, product: AdminProductList) => {
                if (orders?.length) {
                    const orderColor = orders[0].status === AdminOrderStatusType.OrderStatusPaid ? 'green' : 'grey';

                    return (
                        <>
                            <Tag color={orderColor}>
                                {orders[0].marketplace} ${orders[0].salePrice}{' '}
                                {orderPaymentTypeLabel(orders[0].paymentType)}
                            </Tag>
                            {orders?.length > 1 && <Tag color={'orange'}>{orders.length - 1} more orders</Tag>}
                        </>
                    );
                } else {
                    return <></>;
                }
            },
        },
        {
            title: 'Category',
            dataIndex: 'category',
            key: 'categoryIDs',
            filteredValue: tableParams.filters?.categoryIDs || null,
            filters: categories?.map((category) => ({text: category.name, value: category.id})),
            filterSearch: true,
        },

        {
            title: 'Pallet Order',
            dataIndex: 'palletOrderID',
            key: 'palletOrderIDs',
            filteredValue: tableParams.filters?.palletOrderIDs || null,
            filters: pallets?.map((p) => ({text: p.name, value: p.id})),
            render: (id) => {
                const pallet = palletOrdersQuery.data?.items?.find((p) => p.id === id);
                if (pallet) {
                    return (
                        <a onClick={() => navigate(`/pallet-orders/${id}`)}>
                            {pallet?.name} - {dayjs(pallet?.orderDate).format('MM/DD/YYYY')}
                        </a>
                    );
                }
            },
            filterSearch: true,
        },
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
            render: (text) => (
                <div style={{maxWidth: 300, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>
                    {text}
                </div>
            ),
        },
        {
            title: 'Created At',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (date) => dayjs(date).format('MM/DD/YYYY hh:mm A'),
        },
    ];

    return (
        <div>
            <Flex
                justify="space-between"
                align={'center'}
                style={{marginBottom: '1rem'}}
            >
                <Flex align={'center'}>
                    <Title
                        level={screenMap.lg ? 3 : 5}
                        style={{margin: '0 10px 0 0', display: screenMap.lg ? 'inline-block' : 'none'}}
                    >
                        Products
                    </Title>
                    <Button
                        type="primary"
                        size={screenMap.lg ? 'middle' : 'small'}
                        onClick={() => {
                            navigate('/products/new');
                        }}
                        icon={!screenMap.lg ? <PlusOutlined /> : undefined}
                    >
                        {screenMap.lg ? 'New Product' : undefined}
                    </Button>
                </Flex>
                <Flex align={'center'}>
                    <Flex align={'center'}>
                        <Button
                            type="link"
                            color={'primary'}
                            onClick={clearFilters}
                            disabled={tableParams.filters === undefined && !searchValue}
                            icon={!screenMap.lg ? <CloseOutlined color={'grey'} /> : undefined}
                        >
                            {screenMap.lg ? 'Clear Filters' : undefined}
                        </Button>
                        <Search
                            placeholder={screenMap.lg ? 'Search by Name/Listing №/ASIN' : 'Search'}
                            onSearch={(value) => {
                                setSearchValue(value);
                            }}
                            enterButton
                            size={screenMap.lg ? 'middle' : 'small'}
                            style={{width: !screenMap.lg ? 150 : 300}}
                            allowClear
                            onClear={() => setSearchValue(undefined)}
                            value={searchValue}
                            onChange={(e) => {
                                console.log(e.target.value);
                                setSearchValue(e.target.value);
                            }}
                        />
                    </Flex>
                    <Dropdown menu={{items: actionList, onClick: onClickActions}}>
                        <Button
                            size={screenMap.lg ? 'middle' : 'small'}
                            style={{marginLeft: '1rem'}}
                        >
                            <Space>
                                Actions
                                <MoreOutlined />
                            </Space>
                        </Button>
                    </Dropdown>
                </Flex>
            </Flex>
            <Table<AdminProductList>
                rowKey={(record) => record.id}
                columns={columns}
                key={'id'}
                dataSource={products}
                loading={productsQuery.isLoading || categoryQuery.isLoading}
                scroll={{x: 'max-content'}}
                onChange={handleTableChange}
                pagination={{
                    ...tableParams.pagination,
                    showSizeChanger: true,
                    pageSizeOptions: PAGE_SIZE_OPTIONS,
                }}
                size={'small'}
                rowSelection={rowSelection}
                expandable={{
                    expandedRowRender: (record: AdminProductList) => (
                        <>
                            <Flex justify={'space-between'}>
                                <div style={{minWidth: 380, marginRight: 20}}>
                                    <Card
                                        title={'Marketplaces'}
                                        size={'small'}
                                    >
                                        {!record.marketplaces || record.marketplaces?.length === 0 ? (
                                            <>No marketplaces</>
                                        ) : (
                                            <List
                                                rowKey={(item) => item.id}
                                                key={'id'}
                                                size="small"
                                                dataSource={record.marketplaces}
                                                renderItem={(item) => (
                                                    <List.Item>
                                                        <a
                                                            href={item.link}
                                                            target={'_blank'}
                                                        >
                                                            {item.name}
                                                        </a>{' '}
                                                        Price: <b>${item.price}</b>, CreatedAt:{' '}
                                                        {dayjs(item.createdAt).format('MM/DD/YYYY')}
                                                        <BrokenLinkTag marketplace={item} />
                                                    </List.Item>
                                                )}
                                            />
                                        )}
                                    </Card>
                                </div>

                                {record.images?.length === 0 ? (
                                    <>No images</>
                                ) : (
                                    <Flex
                                        gap={5}
                                        align={'stretch'}
                                        style={{maxHeight: 200}}
                                    >
                                        {record.images?.length! > 1 && (
                                            <Flex
                                                wrap
                                                gap="small"
                                                style={{maxWidth: 200}}
                                            >
                                                {record.images?.slice(1).map((img) => (
                                                    <Image
                                                        key={img.originalPath}
                                                        width={90}
                                                        height={90}
                                                        src={img.thumbnailPath}
                                                        preview={{
                                                            src: img.originalPath,
                                                        }}
                                                    />
                                                ))}
                                            </Flex>
                                        )}
                                        <Image
                                            height={'100%'}
                                            src={record.images?.[0].thumbnailPath}
                                            preview={{
                                                src: record.images?.[0].originalPath,
                                            }}
                                        />
                                    </Flex>
                                )}
                            </Flex>
                        </>
                    ),
                    rowExpandable: (record) => !!record.marketplaces?.length || record.images?.length! > 0,
                }}
            />
            {modalBatchLabelPrint && (
                <BatchLabelPrint
                    products={
                        products
                            ?.filter((p) => selectedRowKeys.includes(p.id))
                            .map(
                                (p: AdminProductList): PrintableProducts => ({
                                    asin: p.listingNumber.toString(),
                                    description: `${p.name.trim().slice(0, 120)}${p.name.length > 120 && '...'}`,
                                })
                            ) || []
                    }
                    close={() => {
                        setModalBatchLabelPrint(false);
                    }}
                />
            )}
            {modalGroupSaleAssign && (
                <GroupSaleAssignModal
                    products={
                        products
                            ?.filter((p) => selectedRowKeys.includes(p.id))
                            .map(
                                (p: AdminProductList): ModalGroupSaleProduct => ({
                                    id: p.id,
                                    name: p.name,
                                    listingNumber: p.listingNumber.toString(),
                                    asin: p.asin || '',
                                })
                            ) || []
                    }
                    onClosed={() => {
                        setModalGroupSaleAssign(false);
                        requestFilters();
                    }}
                />
            )}
            {modalBatchStatusChange && (
                <BatchStatusChangeModal
                    products={
                        products
                            ?.filter((p) => selectedRowKeys.includes(p.id))
                            .map(
                                (p: AdminProductList): ModalStatusProduct => ({
                                    id: p.id,
                                    name: p.name,
                                    listingNumber: p.listingNumber.toString(),
                                    status: p.status,
                                })
                            ) || []
                    }
                    onClosed={(changed) => {
                        setModalBatchStatusChange(false);
                        if (changed) {
                            requestFilters();
                        }
                    }}
                />
            )}
        </div>
    );
};
